import React, { useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import countryList from "react-select-country-list";
import { useEffect } from "react";
import { Card, Form, Spinner, Row, Col } from "react-bootstrap";
import { Pie } from "react-chartjs-2";
import chroma from "chroma-js";
import NotyfContext from "../../contexts/NotyfContext";
import { getNetworkVolumeDistribution } from "../../redux/slices/dashboard";
import Loader from "../spinners/Loader";

const DataVolumeNetworkPieChart = ({ reload, demoMode = false }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [volumeNetworkDistribution, setVolumeNetworkDistribution] = useState(
    []
  );
  const [country, setCountry] = useState("Greece");
  const { user } = useSelector((state) => state.user);
  const { impersonationUser } = useSelector((state) => state.impersonationUser);
  const whitelabel = useSelector((state) => state.whitelabel.whitelabel);
  const [activeUser, setActiveUser] = useState({});
  const isDemoOrLead = demoMode || activeUser.role === "lead";
  const [isLoading, setIsLoading] = useState(!isDemoOrLead);
  const countries = countryList().getData();
  const [bgColor, setBgColor] = useState("#0E1A2B"); // assuming white as default
  const notyf = useContext(NotyfContext);
  const volume_by_network = useSelector(
    (state) => state.dashboard.volume_by_network
  );

  useEffect(() => {
    setIsLoading(!isDemoOrLead);
  }, [isDemoOrLead]);

  const staticVolumeByNetwork = [
    { network: "AT&T (USA)", count: 850_000_000 },
    { network: "Verizon (USA)", count: 780_000_000 },
    { network: "Deutsche Telekom (Germany)", count: 600_000_000 },
    { network: "Vodafone (UK)", count: 550_000_000 },
    { network: "Orange (France)", count: 500_000_000 },
    { network: "NTT Docomo (Japan)", count: 450_000_000 },
    { network: "China Mobile (China)", count: 1_200_000_000 },
    { network: "Telstra (Australia)", count: 320_000_000 },
  ];

  useEffect(() => {
    setActiveUser(impersonationUser.id ? impersonationUser : user);
  }, [user.id, impersonationUser.id]);

  useEffect(() => {
    setBgColor(whitelabel?.backgroundColor ?? "#0E1A2B");
  }, [activeUser]);

  const [scale, setScale] = useState("");

  useEffect(() => {
    if (bgColor) {
      setScale(() =>
        chroma.scale([
          chroma(bgColor).set("hsl.h", "-30"),
          bgColor,
          chroma(bgColor).set("hsl.h", "+30"),
        ])
      );
    }
  }, [bgColor]);
  const [colors, setColors] = useState([]);

  useEffect(() => {
    const fetchVolumeNetworkDistribution = async () => {
      setIsLoading(true);
      await dispatch(getNetworkVolumeDistribution({ country: country }))
        .catch((error) => {
          notyf.open({
            type: "danger",
            message: error,
            duration: 5000,
            ripple: true,
            dismissible: false,
            position: {
              x: "center",
              y: "top",
            },
          });
        })
        .finally(() => {
          setTimeout(() => {
            setIsLoading(false);
          }, [500]);
        });
    };

    if (activeUser.id && demoMode === false && activeUser.role !== "lead") {
      fetchVolumeNetworkDistribution();
    }
  }, [activeUser, country, reload]);

  useEffect(() => {
    if (scale) {
      setColors(
        Array.from(
          {
            length: !isDemoOrLead
              ? volume_by_network.length
              : staticVolumeByNetwork.length,
          },
          (_, i) =>
            scale(
              i /
                (!isDemoOrLead
                  ? volume_by_network.length
                  : staticVolumeByNetwork.length)
            ).hex()
        )
      );
    }
  }, [volume_by_network, activeUser.id, scale]);

  const data = {
    labels: !isDemoOrLead
      ? volume_by_network.map((item) => item.network)
      : staticVolumeByNetwork.map((item) => item.network),
    datasets: [
      {
        data: !isDemoOrLead
          ? volume_by_network.map((item) => item.count / 1000.0 / 1000.0)
          : staticVolumeByNetwork.map((item) => item.count / 1000.0 / 1000.0),
        backgroundColor: colors,
        borderColor: "transparent",
      },
    ],
  };

  const options = {
    maintainAspectRatio: false,
    legend: {
      display: true,
      position: "right",
    },
    tooltips: {
      callbacks: {
        label: function (tooltipItem, data) {
          let dataItem =
            data.datasets[tooltipItem.datasetIndex].data[tooltipItem.index];
          return (
            data.labels[tooltipItem.index] + ": " + dataItem.toFixed(2) + "MB"
          );
        },
      },
    },
  };

  return (
    <Card id="pieChart">
      <Card.Header>
        <h4 className="card-title">{t("dashboard.dataVolumePerNetwork")}</h4>
      </Card.Header>
      <Card.Body>
        {isLoading && <Loader />}
        {!isDemoOrLead && !isLoading && volume_by_network.length === 0 && (
          <React.Fragment>
            <Row>
              <Form.Group as={Col}>
                <Form.Label>Select Country</Form.Label>
                <Form.Select
                  id="countrySelect"
                  name="country"
                  className="mb-5"
                  onChange={(e) => setCountry(e.target.value)}
                  value={country}
                >
                  {countries.map((country, index) => (
                    <option key={index} value={country.label}>
                      {country.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Row>
            <Row>
              <Col>
                <div className="text-center">{t("No data found")}</div>
              </Col>
            </Row>
          </React.Fragment>
        )}
        {(isDemoOrLead || (!isLoading && volume_by_network.length > 0)) && (
          <React.Fragment>
            <Row>
              <Form.Group as={Col}>
                <Form.Label>{t("dashboard.selectCountry")}</Form.Label>
                <Form.Select
                  id="countrySelect"
                  name="country"
                  className="mb-5"
                  onChange={(e) => setCountry(e.target.value)}
                  value={country}
                >
                  {countries.map((country, index) => (
                    <option key={index} value={country.label}>
                      {country.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Row>
            <Row>
              <div className="chart chart-sm">
                <Pie data={data} options={options} />
              </div>
            </Row>
          </React.Fragment>
        )}
      </Card.Body>
    </Card>
  );
};

export default DataVolumeNetworkPieChart;
